/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, DoCheck, EventEmitter, Input, KeyValueDiffers, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectChange, MatSelectModule, MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatOptionModule } from '@angular/material/core';
import { A11yModule } from '@angular/cdk/a11y';
import { Overlay } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { closeScrollFactory } from 'crimson/lib/utils/mat-scroll-strategies';
import {
    NvAddKeyboardActivateDirective,
    NvMatSelectKeyboardFixupDirective
} from '../../../directives/nv-mat-keyboard-fixups.directive';
import { CheckboxFocusMonitorDirective } from '../../../directives/nv-mat-spatial-navigation-fixups.directive';
import { NvHigListItemTrailData, NvHigListItemTrailingElementType } from '../../../models/hig-list-item.types';
import { NvListItem } from '../../../models/hig-modal-shared.types';
import { NvGenericDialog } from '../../nv-generic-dialog/nv-generic-dialog';
import { NvGenericDialogData } from '../../nv-generic-dialog/nv-generic-dialog.types';
import { NvHigRotaryComponent } from '../../nv-hig-rotary/nv-hig-rotary.component';

@Component({
    selector: 'nv-hig-list-item-trail',
    templateUrl: './nv-hig-list-item-trail.component.html',
    styleUrl: './nv-hig-list-item-trail.component.scss',
    imports: [
        MatIconModule,
        MatCheckboxModule,
        NvAddKeyboardActivateDirective,
        CheckboxFocusMonitorDirective,
        FormsModule,
        MatSlideToggleModule,
        MatButtonModule,
        NgTemplateOutlet,
        NvHigRotaryComponent,
        MatSelectModule,
        MatFormFieldModule,
        NvMatSelectKeyboardFixupDirective,
        A11yModule,
        MatOptionModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        TranslateModule
    ],
    providers: [
        {
            provide: MAT_SELECT_SCROLL_STRATEGY,
            useFactory: closeScrollFactory,
            deps: [Overlay]
        }
    ]
})
export class NvHigListItemTrailComponent implements DoCheck, OnInit, OnDestroy {
    data: NvHigListItemTrailData;
    ariaLabel: string | null = null;
    primaryButtonAriaLabel: string | null = null;
    secondaryButtonAriaLabel: string | null = null;

    @Input() set cardData(x: NvHigListItemTrailData) {
        this.data = x;

        // Cache aria label
        this.ariaLabel = this.data?.ariaLabel || null;

        // Compute and cache button aria labels
        this.primaryButtonAriaLabel = this.computeButtonAriaLabel(this.data?.primaryButton?.text);
        this.secondaryButtonAriaLabel = this.computeButtonAriaLabel(this.data?.secondaryButton?.text);

        if (this.data?.listItems?.length > 0) {
            this.initializeListItems(true);
            this.objectDifferMap = undefined;
            this.setupKvDiffer();
        }
        if (this.data?.toggleState) {
            this.toggleState = this.data.toggleState;
        }
    }

    @Input() openDropdownAsPopup = false;
    /** TODO: Use CSS vars to control spinner size once we move to Angular v19
     * Angular pre-v19 does not allow CSS overrides of spinners cleanly, and needs
     * to be done via the TS interface
     */
    @Input() spinnerDiameter = 16;
    @Input() dialogPanelClass = '';
    /** Only for deep comparison of objects in this.data.listItem */
    @Input('useDifferForUpdate') useDifferForListItemUpdate = false;

    @Output() primaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() toggleClicked = new EventEmitter<boolean>();
    @Output() selectionChanged = new EventEmitter<NvListItem>();

    trailElementType = NvHigListItemTrailingElementType;
    selection: NvListItem;
    toggleState = false;
    rotaryOptions: string[] = [];
    rotaryInit: number = 0;

    private destroy$: Subject<void> = new Subject();
    private objectDifferMap: object;

    constructor(
        private genericDialog: NvGenericDialog,
        private differs: KeyValueDiffers,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.setupKvDiffer();
    }

    private computeButtonAriaLabel(buttonText: string | undefined): string | null {
        if (!buttonText) {
            return null;
        }

        const translatedText = this.translate.instant(buttonText);
        const suffix = this.ariaLabel;

        return !suffix ? translatedText : `${translatedText}, ${suffix}`;
    }

    ngDoCheck() {
        if (!this.useDifferForListItemUpdate || !(this.data?.listItems?.length > 0)) {
            return;
        }
        let changed = false;
        this.data?.listItems?.forEach(item => {
            const objDiffer = this.objectDifferMap[item.label];
            const objChanges = objDiffer.diff(item);
            if (objChanges) {
                changed = true;
            }
        });

        if (changed) {
            this.initializeListItems();
        }
    }

    primaryButtonClick(event: MouseEvent): void {
        this.primaryButtonClicked.emit(event);
    }

    secondaryButtonClick(event: MouseEvent): void {
        this.secondaryButtonClicked.emit(event);
    }

    toggleClick($event: MatSlideToggleChange | MatCheckboxChange): void {
        this.toggleClicked.emit($event.checked);
    }

    selectionChange($event?: MatSelectChange): void {
        if (this.openDropdownAsPopup) {
            // Open dialog and emit change
            const dialogData: NvGenericDialogData = {
                headerText: this.data.dialogHeader ?? { text: '' },
                radioOptionSelected: this.selection.label,
                radioButtonOptions: this.data.listItems.map(item => ({ value: item.label }))
            };
            const dialogRef = this.genericDialog.open({
                panelClass: this.dialogPanelClass,
                disableClose: false,
                hasBackdrop: true,
                autoFocus: false,
                data: dialogData
            });
            const selection$ = dialogRef.radioButtonClick.pipe(
                take(1),
                tap(() => {
                    const result = dialogData.radioOptionSelected;
                    this.selection = this.data.listItems.find(item => item.label === result);
                    this.emitSelectionChange(this.selection);
                    dialogRef.close();
                })
            );
            const dismiss$ = dialogRef.afterClosed();
            merge(selection$, dismiss$).pipe(take(1), takeUntil(this.destroy$)).subscribe();
        } else {
            this.emitSelectionChange(this.selection);
        }
    }

    rotaryChange(index: number): void {
        this.selection = this.data.listItems[index];
        this.emitSelectionChange(this.selection);
    }

    private emitSelectionChange(val: NvListItem): void {
        for (const item of this.data.listItems) {
            item.selected = false;
        }
        this.selection.selected = true;
        this.selectionChanged.emit(val);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupKvDiffer(): void {
        if (!this.useDifferForListItemUpdate || !(this.data?.listItems?.length > 0) || !!this.objectDifferMap) {
            return;
        }
        this.objectDifferMap = {};
        /** One differ for each object in the array, tracked by item.label */
        this.data?.listItems?.forEach(item => (this.objectDifferMap[item.label] = this.differs.find(item).create()));
    }

    private initializeListItems(reinitRotaryList: boolean = false): void {
        if (reinitRotaryList) {
            this.rotaryOptions = this.data.listItems.map(i => i.label);
        }
        this.selection = this.data.listItems.find(i => i.selected) ?? this.data.listItems[0];
        const idx = this.data.listItems.findIndex(i => i.selected);
        this.rotaryInit = idx !== -1 ? idx : 0;
    }
}
